// pkg195 — payment method 'unknown' displays as Unknown on Payments Applied and the statement — delivered 2026-10-31
// pkg172a — "Send LawPay Link" button in the action bar for issued/partial invoices (LawPayLinkButton, lane 2) — delivered 2026-10-03
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import LawPayLinkButton from '../components/LawPayLinkButton'; // pkg172a
import { activeFirmTimeZone, formatFirmDate } from '../utils/firmDate';


const STATUS_STYLES = {
  draft: { bg: '#fdf6e3', fg: '#b7870a', border: '#f0d68a', label: 'Draft' },
  issued: { bg: '#e8f0fb', fg: '#2f5496', border: '#bcd4ee', label: 'Issued' },
  partial: { bg: '#fdf2e7', fg: '#d68910', border: '#f5c98e', label: 'Partial' },
  paid: { bg: '#e8f7ef', fg: '#1a7a5e', border: '#b8e3cf', label: 'Paid' },
  voided: { bg: '#f0f0f0', fg: '#888', border: '#dcdcdc', label: 'Voided' }
};

// Mini status badges used inline in the Payments Applied table
const PAY_STATUS_STYLES = {
  cleared: { bg: '#e8f7ef', fg: '#1a7a5e', border: '#b8e3cf', label: 'Cleared' },
  pending: { bg: '#fdf6e3', fg: '#b7870a', border: '#f0d68a', label: 'Pending' },
  reversed: { bg: '#fdf0ec', fg: '#B85042', border: '#f5c6c3', label: 'Reversed' },
  refunded: { bg: '#f0f0f0', fg: '#888', border: '#dcdcdc', label: 'Refunded' },
  // Wave 2: soft-void state
  voided: { bg: '#f3f3f3', fg: '#666', border: '#d6d6d6', label: 'Voided' },
};

const METHOD_LABELS = {
  check: 'Check',
  cash: 'Cash',
  ach: 'ACH',
  wire: 'Wire',
  credit_card: 'Card',
  money_order: 'Money Order',
  trust_transfer: 'Trust Transfer',
  other: 'Other',
  unknown: 'Unknown', // pkg195
};

// Wave 3b — trust transaction type labels for the account statement section.
const TRUST_TYPE_LABELS = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  transfer_to_operating: 'Transfer to Operating',
  adjustment: 'Adjustment',
};

const th = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600 };
const td = { padding: '8px 12px', fontFamily: 'Arial, sans-serif', fontSize: 13 };

function StatusBadge({ status, large }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span style={{
      fontSize: large ? 13 : 11, fontWeight: 600, padding: large ? '4px 12px' : '2px 8px', borderRadius: 4,
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
      fontFamily: 'Arial, sans-serif', display: 'inline-block', whiteSpace: 'nowrap'
    }}>{s.label}</span>);

}

function PaymentStatusBadge({ status }) {
  const s = PAY_STATUS_STYLES[status] || PAY_STATUS_STYLES.cleared;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
      fontFamily: 'Arial, sans-serif', display: 'inline-block', whiteSpace: 'nowrap'
    }}>{s.label}</span>);

}

function AccountBadge({ account }) {
  const base = {
    fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
    fontFamily: 'Arial, sans-serif', display: 'inline-block', whiteSpace: 'nowrap'
  };
  if (account === 'operating') {
    return <span style={{ ...base, background: '#e8f0fb', color: '#2f5496', border: '1px solid #bcd4ee' }}>Operating</span>;
  }
  if (account === 'trust') {
    return <span style={{ ...base, background: '#fff8e1', color: '#b7870a', border: '1px solid #f0e0a0' }}>Trust</span>;
  }
  return <span style={{ color: '#ccc' }}>—</span>;
}

function fmtMoney(n) {
  return '$' + (parseFloat(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fmtDate(s) {
  return formatFirmDate(s);
}

function fmtDateTime(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return formatFirmDate(s);
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone: activeFirmTimeZone(),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
  return `${formatFirmDate(s)} at ${time}`;
}

// First name from a full name (best-effort)
function firstName(fullName) {
  if (!fullName) return '';
  return String(fullName).trim().split(/\s+/)[0];
}

// Pre-fill the email subject + body from invoice + firm settings.
// Note: this is only invoked when the email modal opens; the resulting
// strings are stored in emailModal state and sent verbatim to the server
// — they contain REAL client name/email regardless of privacy mode, because
// privacy mode is a display-only concern. Privacy mode shouldn't reach into
// the outbound email body. The on-screen banner reminds the recorder not to
// open the email modal during a tutorial.
function buildEmailTemplate(invoice, firm) {
  const firmName = firm && firm.firm_name || 'Your Firm';
  const fName = firstName(invoice.client_name);
  const subject = `Invoice #${invoice.invoice_number} from ${firmName}`;

  const lines = [];
  lines.push(`Dear ${fName || invoice.client_name},`);
  lines.push('');
  lines.push(`Please find attached invoice #${invoice.invoice_number}${invoice.subject ? ` (${invoice.subject})` : ''} for legal services rendered.`);
  lines.push('');
  lines.push(`Amount due: ${fmtMoney(invoice.balance_due ?? invoice.total)}`);
  if (invoice.due_date) lines.push(`Due date: ${fmtDate(invoice.due_date)}`);
  lines.push('');
  lines.push('If you have any questions about this invoice, please do not hesitate to contact me.');
  lines.push('');
  lines.push('Sincerely,');
  lines.push(firmName);
  return { subject, body: lines.join('\n') };
}

export default function AdminInvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [firm, setFirm] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmIssue, setConfirmIssue] = useState(false);
  const [voidingReason, setVoidingReason] = useState(null); // null = not voiding; '' = prompt open

  // Phase 3: after issuing, the API returns trust_balance + suggested_fee_transfer.
  // If trust_balance > 0 we open the fee-transfer prompt. Optional — user can skip.
  const [feeTransferPrompt, setFeeTransferPrompt] = useState(null); // null | { suggested, balance, amount, busy, _err }

  // Edit form state (only used in draft mode)
  const [form, setForm] = useState({
    issue_date: '', due_date: '', subject: '', notes: '', internal_notes: '',
    discount: '', tax: ''
  });

  // Email modal state
  const [emailModal, setEmailModal] = useState(null); // null = closed; object = open with form
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {load(); /* eslint-disable-next-line */}, [id]);

  async function load() {
    setLoading(true);setError('');
    try {
      const [data, firmData] = await Promise.all([
      adminApi.getInvoice(id),
      adminApi.getFirmSettings().catch(() => ({}))]
      );
      setInvoice(data);
      setFirm(firmData || {});
      setForm({
        issue_date: (data.issue_date || '').slice(0, 10),
        due_date: (data.due_date || '').slice(0, 10),
        subject: data.subject || '',
        notes: data.notes || '',
        internal_notes: data.internal_notes || '',
        discount: String(parseFloat(data.discount) || ''),
        tax: String(parseFloat(data.tax) || '')
      });
    } catch (err) {setError(err.message);} finally
    {setLoading(false);}
  }

  async function handleSave() {
    setBusy(true);setError('');setSuccess('');
    try {
      await adminApi.updateInvoice(id, {
        issue_date: form.issue_date || null,
        due_date: form.due_date || null,
        subject: form.subject,
        notes: form.notes,
        internal_notes: form.internal_notes,
        discount: parseFloat(form.discount) || 0,
        tax: parseFloat(form.tax) || 0
      });
      setEditing(false);
      setSuccess('Invoice updated.');
      await load();
    } catch (err) {setError(err.message);} finally
    {setBusy(false);}
  }

  async function handleRemoveLine(type, lineId) {
    if (!window.confirm(`Remove this ${type === 'entry' ? 'time entry' : 'expense'} from the invoice? It will return to the pool of items ready to invoice.`)) return;
    setBusy(true);setError('');
    try {
      await adminApi.removeInvoiceLine(id, type, lineId);
      await load();
    } catch (err) {setError(err.message);} finally
    {setBusy(false);}
  }

  async function handleIssue() {
    setBusy(true);setError('');setSuccess('');
    try {
      const result = await adminApi.issueInvoice(id);
      setConfirmIssue(false);
      setSuccess('Invoice issued.');
      await load();
      // Phase 3: if the client has trust funds, prompt to transfer them
      // toward this newly-issued invoice. Optional — they can dismiss.
      const tb = parseFloat(result?.trust_balance) || 0;
      const suggested = parseFloat(result?.suggested_fee_transfer) || 0;
      if (tb > 0.001 && suggested > 0.001) {
        setFeeTransferPrompt({
          balance: tb,
          suggested,
          amount: suggested.toFixed(2),
          busy: false,
          _err: ''
        });
      }
    } catch (err) {setError(err.message);setConfirmIssue(false);} finally
    {setBusy(false);}
  }

  async function handleSubmitFeeTransfer() {
    if (!feeTransferPrompt) return;
    const amt = parseFloat(feeTransferPrompt.amount);
    if (!amt || amt <= 0) return setFeeTransferPrompt((p) => ({ ...p, _err: 'Amount must be positive.' }));
    setFeeTransferPrompt((p) => ({ ...p, busy: true, _err: '' }));
    try {
      await adminApi.feeTransfer(invoice.client_id, {
        invoice_id: id,
        amount: +amt.toFixed(2)
      });
      setFeeTransferPrompt(null);
      setSuccess(`Transferred $${amt.toFixed(2)} from trust and applied to this invoice.`);
      await load();
    } catch (err) {
      setFeeTransferPrompt((p) => p ? { ...p, busy: false, _err: err.message } : null);
    }
  }

  function handleSkipFeeTransfer() {
    setFeeTransferPrompt(null);
    setSuccess('Invoice issued. Trust funds were not transferred.');
  }

  async function handleVoid() {
    if (!voidingReason || !voidingReason.trim()) {
      setError('A void reason is required.');
      return;
    }
    setBusy(true);setError('');setSuccess('');
    try {
      await adminApi.voidInvoice(id, voidingReason.trim());
      setVoidingReason(null);
      setSuccess('Invoice voided. Line items released, and any payments applied to it have been returned to client credit.');
      await load();
    } catch (err) {setError(err.message);} finally
    {setBusy(false);}
  }

  async function handleDelete() {
    setBusy(true);setError('');
    try {
      await adminApi.deleteInvoice(id);
      navigate('/admin/dashboard/invoices');
    } catch (err) {setError(err.message);setBusy(false);setConfirmDelete(false);}
  }

  async function handleDownload() {
    setBusy(true);setError('');
    try {
      const blob = await adminApi.downloadInvoice(id);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      // Use the masked name in the download filename so a recording that
      // captures the "Recent Downloads" toast or filesystem doesn't expose
      // the real client name.
      const displayName = invoice.client_name || 'Client';
      a.download = `Invoice_${invoice.invoice_number}_${displayName.replace(/[^a-zA-Z0-9]/g, '_')}.docx`;
      a.click();
    } catch (err) {setError(err.message);} finally
    {setBusy(false);}
  }

  function handleOpenEmailModal() {
    if (!invoice) return;
    // The template uses REAL client name/email because that's what will
    // actually be sent. The user shouldn't open this modal during recording;
    // the banner is the reminder.
    const tmpl = buildEmailTemplate(invoice, firm);
    setEmailModal({
      to: invoice.client_email || '',
      subject: tmpl.subject,
      body: tmpl.body,
      sendCopyToFirm: true
    });
  }

  async function handleSendEmail() {
    if (!emailModal) return;
    if (!emailModal.to.trim()) return setError('Recipient email is required.');
    if (!emailModal.subject.trim()) return setError('Subject is required.');
    if (!emailModal.body.trim()) return setError('Message body is required.');
    setEmailing(true);setError('');setSuccess('');
    try {
      const result = await adminApi.emailInvoice(id, {
        to: emailModal.to.trim(),
        subject: emailModal.subject,
        body: emailModal.body,
        sendCopyToFirm: emailModal.sendCopyToFirm
      });
      setEmailModal(null);
      setSuccess(`Invoice emailed to ${result.sent_to}${result.bcc?.length ? ` (BCC: ${result.bcc.join(', ')})` : ''}.`);
      await load();
    } catch (err) {setError(err.message);} finally
    {setEmailing(false);}
  }

  if (loading) return <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', fontSize: 13 }}>Loading...</div>;
  if (!invoice) return <div className="alert alert-error">{error || 'Invoice not found'}</div>;

  const isDraft = invoice.status === 'draft';
  const isIssued = invoice.status === 'issued' || invoice.status === 'partial';
  const isVoided = invoice.status === 'voided';
  const isPaid = invoice.status === 'paid';
  const canEdit = isDraft;
  const canIssue = isDraft && (invoice.entries?.length > 0 || invoice.expenses?.length > 0);
  const canDelete = isDraft;
  const canVoid = isIssued || isPaid;
  const canEmail = isIssued || isPaid;
  const canDownload = !isDraft || true; // allow draft too — useful for review/preview
  const sentCount = parseInt(invoice.sent_count) || 0;

  // Phase 2: payments applied to this invoice
  const allocations = invoice.allocations || [];
  const allocTotal = allocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-sm" onClick={() => navigate('/admin/dashboard/invoices')}>← Invoices</button>
        <button className="btn btn-sm" onClick={() => navigate(`/admin/clients/${invoice.client_id}`)}>View Client</button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: 6 }}>
            Invoice #{invoice.invoice_number} <StatusBadge status={invoice.status} large />
          </h1>
          <p className="page-subtitle">
            {invoice.client_name}
            {invoice.created_by_name && <> &nbsp;·&nbsp; Created by {invoice.created_by_name}</>}
            &nbsp;·&nbsp; Created {fmtDate(invoice.created_at)}
          </p>
          {sentCount > 0 &&
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#1a7a5e', margin: '4px 0 0' }}>
              ✓ Emailed to client {sentCount === 1 ? 'once' : `${sentCount} times`} · last on {fmtDateTime(invoice.last_sent_at)}
            </p>
          }
        </div>
        <div className="btn-row" style={{ margin: 0, flexWrap: 'wrap' }}>
          {canDownload && <button className="btn" onClick={handleDownload} disabled={busy}>⬇ Download Word</button>}
          {canEmail && <button className="btn btn-primary" onClick={handleOpenEmailModal} disabled={busy}>✉ Email to Client</button>}
          {/* pkg172a — emailed LawPay link, operating page, amount locked to the balance */}
          {(invoice.status === 'issued' || invoice.status === 'partial') &&
            <LawPayLinkButton purpose="invoice" clientId={invoice.client_id} clientEmail={invoice.client_email}
              invoiceId={invoice.id} invoiceNumber={invoice.invoice_number} balanceDue={invoice.balance_due} />}
          {canIssue && <button className="btn btn-primary" onClick={() => setConfirmIssue(true)} disabled={busy}>Issue Invoice</button>}
          {canVoid && <button className="btn" onClick={() => setVoidingReason('')} disabled={busy} style={{ borderColor: '#B85042', color: '#B85042' }}>Void Invoice</button>}
          {canDelete && <button className="btn" onClick={() => setConfirmDelete(true)} disabled={busy} style={{ borderColor: '#B85042', color: '#B85042' }}>Delete Draft</button>}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {isVoided && invoice.void_reason &&
      <div style={{ background: '#fdf0ec', border: '1px solid #f5c6c3', borderRadius: 4, padding: 12, marginBottom: 16, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
          <strong style={{ color: '#B85042' }}>Voided:</strong> {invoice.void_reason}
          {invoice.voided_at && <span style={{ color: '#888', marginLeft: 8 }}>· {fmtDate(invoice.voided_at)}</span>}
        </div>
      }

      {/* Email modal */}
      {/* Note: the To input is initialized with the real client email so
           sending works correctly. Privacy mode only masks the read-only
           "Reset to client email (...)" link text below. Don't open this
           modal during a recording. */}
      {emailModal &&
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflow: 'auto'
      }} onClick={(e) => {if (e.target === e.currentTarget && !emailing) setEmailModal(null);}}>
          <div style={{
          background: '#fff', borderRadius: 6, width: '100%', maxWidth: 640, padding: 24,
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)', fontFamily: 'Arial, sans-serif'
        }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px' }}>Email Invoice #{invoice.invoice_number}</h2>
            <p style={{ fontSize: 12, color: '#888', margin: '0 0 20px' }}>
              The Word .docx file will be attached. Edit the message below before sending.
            </p>

            <div className="form-group">
              <label>To</label>
              <input type="email" value={emailModal.to}
            onChange={(e) => setEmailModal((p) => ({ ...p, to: e.target.value }))}
            placeholder="recipient@example.com" disabled={emailing} />
              {invoice.client_email && emailModal.to !== invoice.client_email &&
            <button type="button"
            onClick={() => setEmailModal((p) => ({ ...p, to: invoice.client_email }))}
            style={{ background: 'none', border: 'none', color: '#2f5496', fontSize: 11, cursor: 'pointer', padding: '4px 0', textDecoration: 'underline' }}>
                  Reset to client email ({invoice.client_email})
                </button>
            }
            </div>
            <div className="form-group">
              <label>Subject</label>
              <input type="text" value={emailModal.subject}
            onChange={(e) => setEmailModal((p) => ({ ...p, subject: e.target.value }))}
            disabled={emailing} />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea rows={10} value={emailModal.body}
            onChange={(e) => setEmailModal((p) => ({ ...p, body: e.target.value }))}
            disabled={emailing}
            style={{ fontFamily: 'Georgia, serif', fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13 }}>
              <input type="checkbox" id="bcc-firm"
            checked={emailModal.sendCopyToFirm}
            onChange={(e) => setEmailModal((p) => ({ ...p, sendCopyToFirm: e.target.checked }))}
            disabled={emailing} />
              <label htmlFor="bcc-firm" style={{ margin: 0, cursor: 'pointer', fontWeight: 'normal' }}>
                BCC firm contact email
                {firm.client_contact_email && <span style={{ color: '#888' }}> ({firm.client_contact_email})</span>}
              </label>
            </div>
            <div className="btn-row" style={{ margin: 0 }}>
              <button className="btn" onClick={() => setEmailModal(null)} disabled={emailing}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSendEmail} disabled={emailing}>
                {emailing ? 'Sending...' : `Send to ${emailModal.to || 'client'}`}
              </button>
            </div>
          </div>
        </div>
      }

      {/* Confirm modals */}
      {confirmIssue &&
      <div className="card" style={{ borderColor: '#2f5496', marginBottom: 16 }}>
          <div className="card-title">Issue this invoice?</div>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 12 }}>
            Once issued, the line items become locked. To change anything afterward you'll need to void the invoice and start over.
          </p>
          <div className="btn-row">
            <button className="btn" onClick={() => setConfirmIssue(false)} disabled={busy}>Cancel</button>
            <button className="btn btn-primary" onClick={handleIssue} disabled={busy}>{busy ? 'Issuing...' : 'Yes, Issue Invoice'}</button>
          </div>
        </div>
      }

      {voidingReason !== null &&
      <div className="card" style={{ borderColor: '#B85042', marginBottom: 16 }}>
          <div className="card-title">Void this invoice?</div>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 8 }}>
            All line items will be released and can be invoiced again.
            {allocTotal > 0.001 &&
          <> Any payments applied to this invoice (<strong>{fmtMoney(allocTotal)}</strong>) will be returned to the client's credit balance.</>
          }{' '}
            The invoice itself stays in the system as a record of the void.
          </p>
          <div className="form-group">
            <label>Reason for voiding (required)</label>
            <input type="text" value={voidingReason} onChange={(e) => setVoidingReason(e.target.value)}
          placeholder="e.g. Issued in error — wrong matter description" autoFocus />
          </div>
          <div className="btn-row">
            <button className="btn" onClick={() => setVoidingReason(null)} disabled={busy}>Cancel</button>
            <button className="btn" onClick={handleVoid} disabled={busy} style={{ background: '#B85042', color: '#fff', borderColor: '#B85042' }}>
              {busy ? 'Voiding...' : 'Yes, Void Invoice'}
            </button>
          </div>
        </div>
      }

      {confirmDelete &&
      <div className="card" style={{ borderColor: '#B85042', marginBottom: 16 }}>
          <div className="card-title">Delete this draft?</div>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 12 }}>
            The draft will be permanently deleted. Line items will be released and can be invoiced again. This cannot be undone.
          </p>
          <div className="btn-row">
            <button className="btn" onClick={() => setConfirmDelete(false)} disabled={busy}>Cancel</button>
            <button className="btn" onClick={handleDelete} disabled={busy} style={{ background: '#B85042', color: '#fff', borderColor: '#B85042' }}>
              {busy ? 'Deleting...' : 'Yes, Delete'}
            </button>
          </div>
        </div>
      }

      {/* Phase 3: Fee-transfer prompt — opens automatically after issuing
           when the client has trust funds. Optional; can be skipped. */}
      {feeTransferPrompt && (() => {
        const ftp = feeTransferPrompt;
        return (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '40px 20px', overflow: 'auto'
          }} onClick={(e) => {if (e.target === e.currentTarget && !ftp.busy) handleSkipFeeTransfer();}}>
            <div style={{
              background: '#fff', borderRadius: 6, width: '100%', maxWidth: 560, padding: 24,
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)', fontFamily: 'Arial, sans-serif'
            }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px' }}>Transfer Earned Fees from Trust?</h2>
              <p style={{ fontSize: 12, color: '#888', margin: '0 0 16px' }}>
                This client has <strong style={{ color: '#1a7a5e' }}>{fmtMoney(ftp.balance)}</strong> in trust.
                You can transfer up to {fmtMoney(ftp.suggested)} (the lesser of the trust balance and this invoice's total)
                directly to your operating account and apply it as payment toward this invoice in one step.
              </p>
              {ftp._err && <div className="alert alert-error">{ftp._err}</div>}
              <div className="form-group">
                <label>Transfer Amount ($)</label>
                <input type="number" min="0.01" max={ftp.suggested} step="0.01" value={ftp.amount}
                onChange={(e) => setFeeTransferPrompt((p) => ({ ...p, amount: e.target.value, _err: '' }))}
                disabled={ftp.busy} autoFocus />
                <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', margin: '4px 0 0' }}>
                  Capped at {fmtMoney(ftp.suggested)}. Pre-filled with the suggested amount.
                </p>
              </div>
              <div style={{
                background: '#fdf6e3', border: '1px solid #f0d68a', borderRadius: 4,
                padding: '8px 12px', marginBottom: 16, fontSize: 12
              }}>
                Skipping is fine — trust funds stay in trust until you transfer them.
                You can do this later from the client's billing tab.
              </div>
              <div className="btn-row" style={{ margin: 0 }}>
                <button className="btn" onClick={handleSkipFeeTransfer} disabled={ftp.busy}>Skip</button>
                <button className="btn btn-primary" onClick={handleSubmitFeeTransfer} disabled={ftp.busy}>
                  {ftp.busy ? 'Transferring...' : 'Transfer & Apply'}
                </button>
              </div>
            </div>
          </div>);

      })()}

      {/* Phase 3: post-issue fee-transfer prompt. Only shown if the client has
           a positive trust balance at the moment of issue. Optional — skipping
           is allowed and a yellow warning explains the choice. */}
      {feeTransferPrompt &&
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 20px', overflow: 'auto'
      }} onClick={(e) => {if (e.target === e.currentTarget && !feeTransferPrompt.busy) handleSkipFeeTransfer();}}>
          <div style={{
          background: '#fff', borderRadius: 6, width: '100%', maxWidth: 540, padding: 24,
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)', fontFamily: 'Arial, sans-serif'
        }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px' }}>Transfer Earned Fees from Trust?</h2>
            <p style={{ fontSize: 13, color: '#555', margin: '0 0 16px', lineHeight: 1.5 }}>
              This client has <strong>${feeTransferPrompt.balance.toFixed(2)}</strong> in trust. You can transfer
              up to <strong>${feeTransferPrompt.suggested.toFixed(2)}</strong> from trust to your operating account
              and apply it against this invoice in one step.
            </p>
            {feeTransferPrompt._err && <div className="alert alert-error">{feeTransferPrompt._err}</div>}
            <div className="form-group">
              <label>Amount to Transfer ($)</label>
              <input type="number" min="0.01" step="0.01" max={feeTransferPrompt.suggested}
            value={feeTransferPrompt.amount}
            onChange={(e) => setFeeTransferPrompt((p) => ({ ...p, amount: e.target.value, _err: '' }))}
            disabled={feeTransferPrompt.busy} autoFocus />
              <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>
                Capped at the lesser of trust balance and invoice total. Defaults to the suggested amount.
              </p>
            </div>
            <div style={{
            padding: 10, borderRadius: 4, background: '#fdf6e3', border: '1px solid #f0d68a',
            fontSize: 12, color: '#7a5b08', margin: '12px 0', lineHeight: 1.5
          }}>
              <strong>Skipping this step is fine</strong> if the trust funds are earmarked for something else
              (e.g. court costs, expert witness retainer). You can always do the transfer later from the
              client's billing tab.
            </div>
            <div className="btn-row" style={{ margin: 0 }}>
              <button className="btn" onClick={handleSkipFeeTransfer} disabled={feeTransferPrompt.busy}>
                Skip — don't transfer
              </button>
              <button className="btn btn-primary" onClick={handleSubmitFeeTransfer}
            disabled={feeTransferPrompt.busy}>
                {feeTransferPrompt.busy ? 'Transferring...' : 'Transfer & Apply to Invoice'}
              </button>
            </div>
          </div>
        </div>
      }

      {/* Header info */}
      <div className="section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Invoice Details</span>
        {canEdit && !editing && <button className="btn btn-sm" onClick={() => setEditing(true)}>Edit</button>}
        {editing &&
        <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" onClick={() => {setEditing(false);load();}} disabled={busy}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={busy}>{busy ? 'Saving...' : 'Save'}</button>
          </div>
        }
      </div>
      <div className="card">
        {editing ?
        <>
            <div className="form-row">
              <div className="form-group">
                <label>Issue Date</label>
                <input type="date" value={form.issue_date} onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label>Subject</label>
              <input type="text" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Optional" />
            </div>
            <div className="form-group">
              <label>Notes <span style={{ fontWeight: 400, color: '#888' }}>(visible on the invoice)</span></label>
              <textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Internal Notes <span style={{ fontWeight: 400, color: '#888' }}>(staff only)</span></label>
              <textarea rows={2} value={form.internal_notes} onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: '0 0 140px' }}>
                <label>Discount ($)</label>
                <input type="number" min="0" step="0.01" value={form.discount} onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value }))} />
              </div>
              <div className="form-group" style={{ flex: '0 0 140px' }}>
                <label>Tax ($)</label>
                <input type="number" min="0" step="0.01" value={form.tax} onChange={(e) => setForm((f) => ({ ...f, tax: e.target.value }))} />
              </div>
            </div>
          </> :

        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: invoice.subject || invoice.notes ? 16 : 0 }}>
              <div>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Issue Date</div>
                <div style={{ fontWeight: 500 }}>{fmtDate(invoice.issue_date)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Due Date</div>
                <div style={{ fontWeight: 500 }}>{fmtDate(invoice.due_date)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Bill To</div>
                <div style={{ fontWeight: 500 }}>{invoice.client_name}</div>
                {invoice.client_email && <div style={{ color: '#666', fontSize: 12 }}>{invoice.client_email}</div>}
              </div>
            </div>
            {invoice.subject &&
          <div>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Subject</div>
                <div>{invoice.subject}</div>
              </div>
          }
            {invoice.notes &&
          <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{invoice.notes}</div>
              </div>
          }
            {invoice.internal_notes &&
          <div style={{ marginTop: 12, padding: 10, background: '#fff8e1', borderRadius: 4, border: '1px solid #f0e0a0' }}>
                <div style={{ fontSize: 11, color: '#b7870a', textTransform: 'uppercase', marginBottom: 4, fontWeight: 600 }}>Internal Notes (staff only)</div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{invoice.internal_notes}</div>
              </div>
          }
          </div>
        }
      </div>

      {/* Time entries — Wave 2: non-billable entries appear as "No Charge"
          lines so the client sees the work that was performed at no cost. */}
      {invoice.entries?.length > 0 &&
      <>
          {(() => {
            const billableCount    = invoice.entries.filter((e) => e.billable !== false).length;
            const nonBillableCount = invoice.entries.length - billableCount;
            return (
              <div className="section-head">
                Time Entries ({invoice.entries.length})
                {nonBillableCount > 0 &&
                <span style={{ fontSize: 12, fontWeight: 400, color: '#888', marginLeft: 8 }}>
                  · {billableCount} billable · {nonBillableCount} no-charge
                </span>
                }
              </div>
            );
          })()}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#0F1838' }}>
                    <th style={{ ...th, width: 100 }}>Date</th>
                    <th style={th}>Description</th>
                    <th style={{ ...th, width: 110 }}>Biller</th>
                    <th style={{ ...th, width: 60, textAlign: 'right' }}>Hrs</th>
                    <th style={{ ...th, width: 80, textAlign: 'right' }}>Rate</th>
                    <th style={{ ...th, width: 90, textAlign: 'right' }}>Amount</th>
                    {canEdit && <th style={{ ...th, width: 50 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {invoice.entries.map((e, i) => {
                  const hrs = parseFloat(e.units) || 0;
                  const rate = e.rate_used ? parseFloat(e.rate_used) : null;
                  const billable = e.billable !== false;
                  const amt = billable && rate ? hrs * rate : null;
                  return (
                    <tr key={e.id} style={{
                      borderBottom: '1px solid #eee',
                      background: i % 2 === 0 ? '#fff' : '#fafafa',
                      fontStyle: billable ? 'normal' : 'italic',
                      color: billable ? '#0F1838' : '#666',
                    }}>
                        <td style={td}>{fmtDate(e.entry_date)}</td>
                        <td style={td}>
                          {!billable &&
                            <span style={{ display: 'inline-block', marginRight: 6, padding: '1px 6px', borderRadius: 3, background: '#fbf7ee', border: '1px solid #ead9ad', color: '#b7870a', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontStyle: 'normal' }}>
                              No Charge
                            </span>
                          }
                          {e.description}
                        </td>
                        <td style={{ ...td, color: '#555', fontSize: 12 }}>{e.biller_name || '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{hrs.toFixed(2)}</td>
                        <td style={{ ...td, textAlign: 'right', color: '#555' }}>{billable && rate ? fmtMoney(rate) : '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: billable ? 600 : 400 }}>
                          {billable ? (amt != null ? fmtMoney(amt) : '—') : <span style={{ color: '#888', fontStyle: 'italic' }}>No charge</span>}
                        </td>
                        {canEdit &&
                      <td style={{ ...td, textAlign: 'center' }}>
                            <button onClick={() => handleRemoveLine('entry', e.id)} disabled={busy}
                        title="Remove from invoice (returns to ready-to-invoice pool)"
                        style={{ background: 'none', border: '1px solid #B85042', color: '#B85042', borderRadius: 3, padding: '2px 6px', cursor: 'pointer', fontSize: 12 }}>×</button>
                          </td>
                      }
                      </tr>);

                })}
                </tbody>
                {(() => {
                  const billableHours = invoice.entries.filter((e) => e.billable !== false).reduce((s, e) => s + (parseFloat(e.units) || 0), 0);
                  const noChargeHours = invoice.entries.filter((e) => e.billable === false).reduce((s, e) => s + (parseFloat(e.units) || 0), 0);
                  if (noChargeHours <= 0) return null;
                  return (
                    <tfoot>
                      <tr style={{ background: '#fafafa', borderTop: '2px solid #ddd' }}>
                        <td colSpan={3} style={{ ...td, fontSize: 12, color: '#888', fontStyle: 'italic' }}>
                          Total no-charge time provided at no cost to client
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontSize: 12, color: '#888' }}>{noChargeHours.toFixed(2)}</td>
                        <td colSpan={canEdit ? 3 : 2} style={{ ...td, textAlign: 'right', fontSize: 12, color: '#888', fontStyle: 'italic' }}>No charge</td>
                      </tr>
                      {billableHours > 0 &&
                      <tr style={{ background: '#fafafa' }}>
                        <td colSpan={3} style={{ ...td, fontWeight: 600, fontSize: 12 }}>Total billable time</td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, fontSize: 12 }}>{billableHours.toFixed(2)}</td>
                        <td colSpan={canEdit ? 3 : 2} style={{ ...td }} />
                      </tr>
                      }
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          </div>
        </>
      }

      {/* Expenses */}
      {invoice.expenses?.length > 0 &&
      <>
          <div className="section-head">Expenses ({invoice.expenses.length})</div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#0F1838' }}>
                    <th style={{ ...th, width: 100 }}>Date</th>
                    <th style={th}>Description</th>
                    <th style={{ ...th, width: 130 }}>Category</th>
                    <th style={{ ...th, width: 60, textAlign: 'right' }}>Qty</th>
                    <th style={{ ...th, width: 90, textAlign: 'right' }}>Unit</th>
                    <th style={{ ...th, width: 90, textAlign: 'right' }}>Amount</th>
                    {canEdit && <th style={{ ...th, width: 50 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {invoice.expenses.map((x, i) => {
                  const qty = parseFloat(x.quantity) || 0;
                  const unit = parseFloat(x.unit_price) || 0;
                  return (
                    <tr key={x.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={td}>{fmtDate(x.expense_date)}</td>
                        <td style={td}>{x.description}</td>
                        <td style={{ ...td, color: '#555', fontSize: 12 }}>{x.category || '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{qty}</td>
                        <td style={{ ...td, textAlign: 'right', color: '#555' }}>{fmtMoney(unit)}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(x.amount)}</td>
                        {canEdit &&
                      <td style={{ ...td, textAlign: 'center' }}>
                            <button onClick={() => handleRemoveLine('expense', x.id)} disabled={busy}
                        title="Remove from invoice (returns to ready-to-invoice pool)"
                        style={{ background: 'none', border: '1px solid #B85042', color: '#B85042', borderRadius: 3, padding: '2px 6px', cursor: 'pointer', fontSize: 12 }}>×</button>
                          </td>
                      }
                      </tr>);

                })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      }

      {!invoice.entries?.length && !invoice.expenses?.length &&
      <div className="card" style={{ textAlign: 'center', color: '#888', padding: 24, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
          No line items on this invoice.
        </div>
      }

      {/* Totals */}
      <div className="section-head">Totals</div>
      <div className="card">
        <div style={{ maxWidth: 360, marginLeft: 'auto', fontFamily: 'Arial, sans-serif', fontSize: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
            <span style={{ color: '#666' }}>Professional Services</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtMoney(invoice.fees_subtotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
            <span style={{ color: '#666' }}>Costs & Expenses</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtMoney(invoice.expenses_subtotal)}</span>
          </div>
          {parseFloat(invoice.discount) > 0 &&
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#666' }}>Discount</span>
              <span style={{ fontFamily: 'monospace' }}>({fmtMoney(invoice.discount)})</span>
            </div>
          }
          {parseFloat(invoice.tax) > 0 &&
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#666' }}>Tax</span>
              <span style={{ fontFamily: 'monospace' }}>{fmtMoney(invoice.tax)}</span>
            </div>
          }
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '2px solid #0F1838', marginTop: 4 }}>
            <span style={{ fontWeight: 700 }}>Total</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18 }}>{fmtMoney(invoice.total)}</span>
          </div>
          {allocTotal > 0.001 &&
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#1a7a5e' }}>
              <span>Less: Payments Applied</span>
              <span style={{ fontFamily: 'monospace' }}>({fmtMoney(allocTotal)})</span>
            </div>
          }
          {(isIssued || isPaid) && Math.abs(parseFloat(invoice.balance_due) - parseFloat(invoice.total)) > 0.001 &&
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid #ccc', marginTop: 6 }}>
              <span style={{ fontWeight: 600 }}>Balance Due</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtMoney(invoice.balance_due)}</span>
            </div>
          }
        </div>
      </div>

      {/* Phase 2: Payments Applied */}
      {allocations.length > 0 &&
      <>
          <div className="section-head">Payments Applied ({allocations.length})</div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#0F1838' }}>
                    <th style={{ ...th, width: 110 }}>Date</th>
                    <th style={{ ...th, width: 110 }}>Method</th>
                    <th style={{ ...th, width: 110 }}>Account</th>
                    <th style={th}>Reference</th>
                    <th style={{ ...th, width: 100 }}>Status</th>
                    <th style={{ ...th, width: 110, textAlign: 'right' }}>Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((a, i) =>
                <tr key={a.allocation_id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={td}>{fmtDate(a.payment_date)}</td>
                      <td style={{ ...td, color: '#555', fontSize: 12 }}>
                        {METHOD_LABELS[a.method] || a.method}
                      </td>
                      <td style={td}><AccountBadge account={a.deposit_account} /></td>
                      <td style={{ ...td, color: '#555', fontSize: 12 }}>
                        {a.reference_number || <span style={{ color: '#ccc' }}>—</span>}
                        {a.payor && <span style={{ color: '#888', fontStyle: 'italic', marginLeft: 6 }}>(via {a.payor})</span>}
                      </td>
                      <td style={td}><PaymentStatusBadge status={a.payment_status} /></td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtMoney(a.amount)}</td>
                    </tr>
                )}
                  <tr style={{ background: '#0F1838', color: '#fff' }}>
                    <td colSpan={5} style={{ ...td, color: '#fff', fontWeight: 700 }}>Total Applied</td>
                    <td style={{ ...td, color: '#fff', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtMoney(allocTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      }

      {/* ── Wave 3a: Account Statement sections ─────────────────────────────
          Mirror what the client will see on the generated docx. Renders only
          if the invoice has a statement window configured (statement_from is
          set). For old/legacy invoices these sections are absent. */}
      {invoice.include_statement &&
      <>
        {(() => {
          const stmtPayments  = invoice.statement_payments  || [];
          const stmtRefunds   = invoice.statement_refunds   || [];
          const stmtReversals = invoice.statement_reversals || [];
          const stmtTrust     = invoice.statement_trust     || [];
          const acctSummary   = invoice.account_summary;
          const hasAny = stmtPayments.length || stmtRefunds.length || stmtReversals.length || stmtTrust.length;
          // Wave 3b: invoice.statement_from may be null = "all time, no
          // lower bound." Render the period accordingly.
          const periodLabel = invoice.statement_from
            ? `${fmtDate(invoice.statement_from)} – ${fmtDate(invoice.statement_to || invoice.issue_date)}`
            : `All activity through ${fmtDate(invoice.statement_to || invoice.issue_date)}`;
          return (
            <>
              <div className="section-head" style={{ marginTop: 28 }}>
                Account Activity
                <span style={{ fontSize: 12, fontWeight: 400, color: '#888', marginLeft: 8 }}>
                  · {periodLabel}
                  {invoice.include_voided && <> · includes voided</>}
                </span>
              </div>
              {!hasAny &&
              <div className="card" style={{ textAlign: 'center', color: '#888', padding: 20, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                No account activity in this statement period.
              </div>
              }

              {stmtPayments.length > 0 &&
              <>
                <div style={{ fontSize: 12, color: '#666', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 12, marginBottom: 6 }}>
                  Payments Received ({stmtPayments.length})
                </div>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div className="table-wrap">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#0F1838' }}>
                          <th style={{ ...th, width: 110 }}>Date</th>
                          <th style={{ ...th, width: 130 }}>Method</th>
                          <th style={{ ...th, width: 140 }}>Ref #</th>
                          <th style={th}>Payor</th>
                          <th style={{ ...th, width: 100, textAlign: 'center' }}>Status</th>
                          <th style={{ ...th, width: 110, textAlign: 'right' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stmtPayments.map((p, i) => {
                          const v = p.status === 'voided';
                          return (
                            <tr key={p.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa', opacity: v ? 0.6 : 1, fontStyle: v ? 'italic' : 'normal' }}>
                              <td style={td}>{fmtDate(p.payment_date)}</td>
                              <td style={td}>{METHOD_LABELS[p.method] || p.method}</td>
                              <td style={{ ...td, fontFamily: 'monospace', color: '#666', fontSize: 12 }}>{p.reference_number || '—'}</td>
                              <td style={{ ...td, color: '#555', fontSize: 12 }}>{p.payor || '—'}</td>
                              <td style={{ ...td, textAlign: 'center' }}><PaymentStatusBadge status={p.status} /></td>
                              <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: v ? 400 : 600, textDecoration: v ? 'line-through' : 'none' }}>{fmtMoney(p.amount)}</td>
                            </tr>);
                        })}
                        <tr style={{ background: '#fafafa' }}>
                          <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 600, fontSize: 12 }}>Total Payments</td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{
                            fmtMoney(stmtPayments.filter((p) => p.status !== 'voided').reduce((s, p) => s + (parseFloat(p.amount) || 0), 0))
                          }</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
              }

              {stmtRefunds.length > 0 &&
              <>
                <div style={{ fontSize: 12, color: '#666', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 12, marginBottom: 6 }}>
                  Refunds Issued ({stmtRefunds.length})
                </div>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div className="table-wrap">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#0F1838' }}>
                          <th style={{ ...th, width: 110 }}>Date</th>
                          <th style={{ ...th, width: 130 }}>Method</th>
                          <th style={{ ...th, width: 140 }}>Ref #</th>
                          <th style={th}>Reason</th>
                          <th style={{ ...th, width: 100, textAlign: 'center' }}>Status</th>
                          <th style={{ ...th, width: 110, textAlign: 'right' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stmtRefunds.map((r, i) => {
                          const v = r.status === 'voided';
                          return (
                            <tr key={r.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa', opacity: v ? 0.6 : 1, fontStyle: v ? 'italic' : 'normal' }}>
                              <td style={td}>{fmtDate(r.refund_date)}</td>
                              <td style={td}>{METHOD_LABELS[r.method] || r.method}</td>
                              <td style={{ ...td, fontFamily: 'monospace', color: '#666', fontSize: 12 }}>{r.reference_number || '—'}</td>
                              <td style={{ ...td, color: '#555', fontSize: 12 }}>{r.reason || '—'}</td>
                              <td style={{ ...td, textAlign: 'center', fontSize: 11, color: v ? '#888' : '#666' }}>{v ? 'VOIDED' : 'Refunded'}</td>
                              <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: v ? 400 : 600, textDecoration: v ? 'line-through' : 'none' }}>{fmtMoney(r.amount)}</td>
                            </tr>);
                        })}
                        <tr style={{ background: '#fafafa' }}>
                          <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 600, fontSize: 12 }}>Total Refunds</td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{
                            fmtMoney(stmtRefunds.filter((r) => r.status !== 'voided').reduce((s, r) => s + (parseFloat(r.amount) || 0), 0))
                          }</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
              }

              {stmtReversals.length > 0 &&
              <>
                <div style={{ fontSize: 12, color: '#666', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 12, marginBottom: 6 }}>
                  Payment Reversals ({stmtReversals.length})
                </div>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div className="table-wrap">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#0F1838' }}>
                          <th style={{ ...th, width: 110 }}>Date</th>
                          <th style={{ ...th, width: 200 }}>Original Payment</th>
                          <th style={{ ...th, width: 130 }}>Method</th>
                          <th style={{ ...th, width: 140 }}>Ref #</th>
                          <th style={th}>Reason</th>
                          <th style={{ ...th, width: 110, textAlign: 'center' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stmtReversals.map((r, i) => {
                          const v = r.status === 'voided';
                          return (
                            <tr key={r.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa', opacity: v ? 0.6 : 1, fontStyle: v ? 'italic' : 'normal' }}>
                              <td style={td}>{fmtDate(r.reversal_date)}</td>
                              <td style={{ ...td, color: '#666', fontSize: 12 }}>
                                {r.source_payment_date ? fmtDate(r.source_payment_date) : '—'}
                                {r.source_payment_amount != null && <> — <span style={{ fontFamily: 'monospace' }}>{fmtMoney(r.source_payment_amount)}</span></>}
                              </td>
                              <td style={td}>{r.method ? (METHOD_LABELS[r.method] || r.method) : '—'}</td>
                              <td style={{ ...td, fontFamily: 'monospace', color: '#666', fontSize: 12 }}>{r.reference_number || '—'}</td>
                              <td style={{ ...td, color: '#555', fontSize: 12 }}>{r.reason || '—'}</td>
                              <td style={{ ...td, textAlign: 'center', fontSize: 11, color: v ? '#888' : '#B85042' }}>{v ? 'VOIDED' : 'Reversed'}</td>
                            </tr>);
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
              }

              {stmtTrust.length > 0 &&
              <>
                <div style={{ fontSize: 12, color: '#666', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 12, marginBottom: 6 }}>
                  Trust Account Activity ({stmtTrust.length})
                </div>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div className="table-wrap">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#0F1838' }}>
                          <th style={{ ...th, width: 110 }}>Date</th>
                          <th style={{ ...th, width: 170 }}>Type</th>
                          <th style={{ ...th, width: 130 }}>Method</th>
                          <th style={th}>Payer / Payee</th>
                          <th style={{ ...th, width: 140 }}>Ref #</th>
                          <th style={{ ...th, width: 110, textAlign: 'right' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stmtTrust.map((t, i) => {
                          const amt = parseFloat(t.amount) || 0;
                          return (
                            <tr key={t.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa', verticalAlign: 'top' }}>
                              <td style={td}>{fmtDate(t.txn_date)}</td>
                              <td style={td}>{TRUST_TYPE_LABELS[t.type] || t.type}</td>
                              <td style={td}>{METHOD_LABELS[t.payment_method] || t.payment_method || '—'}</td>
                              <td style={{ ...td, color: '#555', fontSize: 12 }}>
                                {t.payer_payee || '—'}
                                {t.description && <span style={{ color: '#888', display: 'block', fontSize: 11, marginTop: 2 }}>{t.description}</span>}
                              </td>
                              <td style={{ ...td, fontFamily: 'monospace', color: '#666', fontSize: 12 }}>{t.reference_number || '—'}</td>
                              <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: amt < 0 ? '#B85042' : '#1a7a5e' }}>
                                {amt < 0 ? '(' + fmtMoney(Math.abs(amt)) + ')' : fmtMoney(amt)}
                              </td>
                            </tr>);
                        })}
                        <tr style={{ background: '#fafafa' }}>
                          <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 600, fontSize: 12 }}>Net Trust Activity</td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{(() => {
                            const net = stmtTrust.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
                            return net < 0 ? '(' + fmtMoney(Math.abs(net)) + ')' : fmtMoney(net);
                          })()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
              }

              {acctSummary &&
              <>
                <div className="section-head" style={{ marginTop: 24 }}>Overall Account Summary</div>
                <div className="card">
                  <p style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: 0, fontStyle: 'italic' }}>
                    Current state of the client's account across all matters with the firm.
                  </p>
                  <div style={{ maxWidth: 400, marginLeft: 'auto', fontFamily: 'Arial, sans-serif', fontSize: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span style={{ color: '#666' }}>Trust Account Balance</span>
                      <span style={{ fontFamily: 'monospace' }}>{fmtMoney(acctSummary.trust_balance)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span style={{ color: '#666' }}>Total Owed (All Invoices)</span>
                      <span style={{ fontFamily: 'monospace' }}>{
                        parseFloat(acctSummary.total_outstanding) > 0 ?
                        '(' + fmtMoney(acctSummary.total_outstanding) + ')' :
                        fmtMoney(0)
                      }</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '2px solid #0F1838', marginTop: 4 }}>
                      <span style={{ fontWeight: 700 }}>Net Account Balance</span>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18 }}>{(() => {
                        const net = (parseFloat(acctSummary.trust_balance) || 0) -
                                    (parseFloat(acctSummary.total_outstanding) || 0);
                        return net < 0 ? '(' + fmtMoney(Math.abs(net)) + ')' : fmtMoney(net);
                      })()}</span>
                    </div>
                  </div>
                </div>
              </>
              }
            </>
          );
        })()}
      </>
      }
    </div>);

}